import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ClearCurrentComposition from 'actions/composition/close_current_composition';
import CompositionButtons, { CancelButton } from './composition_buttons';
import CompositionContentType from 'models/composition/composition_content_type';
import createInvalidCompanyNumberError from 'scripts/domain/factories/error/create_invalid_company_number_error';
import Dropdown from 'components/lib/dropdown';
import ErrorTooltip from 'components/lib/error_tooltip';
import { getFormattedPhoneNumbersWithTooltip } from './lib/formatted_phone_numbers';
import {
  getValidationErrors as getPhoneValidationErrors,
  normalizePhoneNumber,
  normalizePhoneNumberWithoutValidation,
} from 'models/phone_number';
import InitiatePhoneCall from 'actions/phone_call/initiate_phone_call';
import PhoneCallCompositionContent from 'models/composition/phone_call_composition_content';
import RemoveEmptyCompositions from 'actions/composition/remove_empty_compositions';
import SendButton from './send_button';
import Tooltip from 'components/common/tooltip';
import UpdateComposition from 'actions/composition/update_composition';
import useExecuteAction from 'components/hooks/use_execute_action';

export function PhoneCallCompositionForm({ companyNumbers, composition, isMinimized, onCancel, onMount, onSubmit }) {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();

  const [companyNumber, setCompanyNumber] = useState(() => composition.content.companyNumber || '');
  const [customerNumber, setCustomerNumber] = useState(() => composition.content.customerNumber || '');

  // Refs let the unmount cleanup and id-swap effect read the latest values without re-running.
  const fieldsRef = useRef({ companyNumber, customerNumber });
  fieldsRef.current = { companyNumber, customerNumber };

  const compositionRef = useRef({ id: composition.id, customerId: composition.customerId });
  // Once the form is cancelled or submitted, suppress the save-on-unmount path so we don't resurrect a draft we just closed.
  const isClosedRef = useRef(false);

  const saveDraft = useCallback(
    ({ id, customerId }) => {
      if (!id) return;
      executeAction(UpdateComposition, {
        compositionId: id,
        customerId,
        newContent: new PhoneCallCompositionContent({
          companyNumber: fieldsRef.current.companyNumber,
          customerNumber: fieldsRef.current.customerNumber,
        }),
      });
    },
    [executeAction]
  );

  // When the composition id swaps under us, save the prior draft and load the new content into local state.
  useEffect(() => {
    const prev = compositionRef.current;
    if (prev.id !== composition.id) {
      if (prev.id && !isClosedRef.current) saveDraft(prev);
      compositionRef.current = { id: composition.id, customerId: composition.customerId };
      isClosedRef.current = false;
      setCompanyNumber(composition.content.companyNumber || '');
      setCustomerNumber(composition.content.customerNumber || '');
    } else {
      compositionRef.current.customerId = composition.customerId;
    }
  }, [composition, saveDraft]);

  useEffect(() => {
    onMount && onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror the legacy componentWillUnmount: final save + RemoveEmptyCompositions, unless cancelled / submitted.
  useEffect(() => {
    return () => {
      const { id, customerId } = compositionRef.current;
      if (!isClosedRef.current && id) {
        saveDraft({ id, customerId });
        executeAction(RemoveEmptyCompositions, customerId);
      }
    };
  }, [saveDraft, executeAction]);

  const handleCompanyChange = useCallback(option => setCompanyNumber(option.value), []);
  const handleCustomerChange = useCallback(e => setCustomerNumber(e.target.value), []);

  const handleCancel = useCallback(
    event => {
      event.preventDefault();
      executeAction(ClearCurrentComposition);
      isClosedRef.current = true;
      onCancel && onCancel();
    },
    [executeAction, onCancel]
  );

  const handleSubmit = useCallback(() => {
    const companyError = getCompanyPhoneError(companyNumber, companyNumbers);
    const customerErrors = getPhoneErrors(customerNumber);
    if (companyError || !_.isEmpty(customerErrors)) return;

    executeAction(InitiatePhoneCall, {
      compositionId: compositionRef.current.id,
      content: {
        companyNumber: normalizePhoneNumberWithoutValidation(companyNumber),
        customerNumber: normalizePhoneNumber(customerNumber),
        type: CompositionContentType.PHONE_CALL,
      },
    });

    isClosedRef.current = true;
    onSubmit && onSubmit();
  }, [companyNumber, companyNumbers, customerNumber, executeAction, onSubmit]);

  const { fromOptions, tooltipMessage } = getFormattedPhoneNumbersWithTooltip(companyNumbers, companyNumber);
  const customerErrors = getPhoneErrors(customerNumber);

  return (
    <div className="phoneCallCompositionForm compositionForm">
      <div className="compositionForm-phoneInput-container compositionForm-formBody">
        <div className="phoneCallCompositionForm-header">{t('Phone call')}</div>
        <div className="compositionForm-phoneInput">
          <div className="compositionForm-phoneLabel">{t('From')}</div>
          <div className="phoneCallCompositionForm-from-container">
            <Tooltip
              className="phoneCallCompositionForm-from-dropdown"
              delay={250}
              message={tooltipMessage}
              position="top"
            >
              <Dropdown
                onChange={handleCompanyChange}
                options={fromOptions}
                placeholder=""
                searchable
                value={companyNumber}
              />
            </Tooltip>
          </div>
        </div>
        <div className="compositionForm-phoneInput">
          <div className="compositionForm-phoneLabel">{t('To')}</div>
          <ErrorTooltip className="compositionForm-phoneNumber-errorChecker" errors={customerErrors}>
            <dd>
              <input
                className="editableInput editableField customerProfile-contactInput compositionForm-phoneNumber phoneCallCompositionForm-customerNumber"
                onChange={handleCustomerChange}
                placeholder="customer number"
                value={customerNumber}
              />
            </dd>
          </ErrorTooltip>
        </div>
      </div>
      <CompositionButtons>
        <CancelButton onClick={handleCancel} />
        <SendButton disabled={isMinimized} onClick={handleSubmit}>
          {t('Call')}
        </SendButton>
      </CompositionButtons>
    </div>
  );
}

PhoneCallCompositionForm.propTypes = {
  companyNumbers: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  composition: PropTypes.object.isRequired,
  isMinimized: PropTypes.bool,
  onCancel: PropTypes.func,
  onMount: PropTypes.func,
  onSubmit: PropTypes.func,
};

function getCompanyPhoneError(companyNumber, companyNumbers) {
  if (!_.find(companyNumbers, { number: companyNumber })) {
    return createInvalidCompanyNumberError(companyNumber).detail;
  }
  return null;
}

function getPhoneErrors(phoneNumber) {
  return getPhoneValidationErrors({ phoneNumber }).map(err => err.detail);
}

export default PhoneCallCompositionForm;
